import api from '../lib/api';

/**
 * Organization Service
 * Handles all organization-related API calls
 */

const organizationService = {
    /**
     * Get all organizations for the current admin
     * @returns {Promise<Array>} List of organizations
     */
    getOrganizations: async () => {
        try {
            const response = await api.get('/organizations');
            return response.data;
        } catch (error) {
            console.error('Error fetching organizations:', error);
            throw error;
        }
    },

    /**
     * Get a specific organization by ID
     * @param {string} organizationId - Organization ID
     * @returns {Promise<Object>} Organization details
     */
    getOrganization: async (organizationId) => {
        try {
            const response = await api.get(`/organizations/${organizationId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching organization:', error);
            throw error;
        }
    },

    /**
     * Create a new organization
     * @param {Object} organizationData - Organization data
     * @param {string} organizationData.name - Organization name
     * @returns {Promise<Object>} Created organization
     */
    createOrganization: async (organizationData) => {
        try {
            const response = await api.post('/organizations', organizationData);
            return response.data;
        } catch (error) {
            console.error('Error creating organization:', error);
            throw error;
        }
    },

    /**
     * Update an organization
     * @param {string} organizationId - Organization ID
     * @param {Object} organizationData - Updated organization data
     * @returns {Promise<Object>} Updated organization
     */
    updateOrganization: async (organizationId, organizationData) => {
        try {
            const response = await api.put(`/organizations/${organizationId}`, organizationData);
            return response.data;
        } catch (error) {
            console.error('Error updating organization:', error);
            throw error;
        }
    },

    /**
     * Delete an organization
     * @param {string} organizationId - Organization ID
     * @returns {Promise<void>}
     */
    deleteOrganization: async (organizationId) => {
        try {
            await api.delete(`/organizations/${organizationId}`);
        } catch (error) {
            console.error('Error deleting organization:', error);
            throw error;
        }
    },

    /**
     * Delete an organization asynchronously (avoids timeouts)
     * @param {string} organizationId - Organization ID
     * @returns {Promise<Object>} Job payload
     */
    deleteOrganizationAsync: async (organizationId) => {
        try {
            const response = await api.delete(`/organizations/${organizationId}`, {
                params: { async_delete: true }
            });
            return response.data;
        } catch (error) {
            console.error('Error deleting organization (async):', error);
            throw error;
        }
    },

    /**
     * Get deletion job status
     * @param {string} jobId - Deletion job ID
     * @returns {Promise<Object>}
     */
    getDeletionStatus: async (jobId) => {
        try {
            const response = await api.get(`/organizations/deletion-status/${jobId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Check if an organization can be safely deleted
     * @param {string} organizationId - Organization ID
     * @returns {Promise<Object>}
     */
    getDeletionEligibility: async (organizationId) => {
        try {
            const response = await api.get(`/organizations/${organizationId}/deletion-check`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
};

export default organizationService;
